import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import {
  BasketFlowScreen,
  DescriptionPanel,
} from '../../../orders/pages/newOrderFlow/configureEquipmentMockup';
import { useApiQuestionnaire } from '../../../orders/pages/newOrderFlow/configureEquipmentMockup/basketApi';
import type { SelectedItem } from '../../../orders/pages/newOrderFlow/configureEquipmentMockup/flow';
import { ConfigureTile } from '../../../orders/components/new-order/configure-equipment/ConfigureTile';
import type {
  LegacyAnswers,
  LegacyQuestion,
} from '../../../orders/components/new-order/configure-equipment/useLegacyQuestionnaire';
import { getQuestions } from '../../../../services/orders/questionsService';
import { getProductsByCodes } from '../../../../services/products/productService';
import type { PatientV2Detail } from './types';
import type { PatientEquipmentConfiguration } from './patientEquipmentConfiguration';
import { PatientEquipmentReview } from './PatientEquipmentReview';
import type { Product } from '../../../../types/product/Product';

const LoadingQuestions = () => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <CircularProgress size={26} aria-label="Carregando perguntas" />
  </Box>
);

const LegacyEquipmentQuestionnaire = ({
  product,
  onConfigurationChange,
}: {
  product: Product;
  onConfigurationChange: (value: PatientEquipmentConfiguration) => void;
}) => {
  const [attempt, setAttempt] = useState(0);
  const [questions, setQuestions] = useState<LegacyQuestion[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [legacy, setLegacy] = useState<{
    answers: LegacyAnswers;
    complete: boolean;
  }>({ answers: {}, complete: false });
  const { answers } = legacy;
  const [extrasAttempt, setExtrasAttempt] = useState(0);
  const [extrasState, setExtrasState] = useState<{
    key: string;
    products: Product[];
    failed: boolean;
    loading: boolean;
  }>({ key: '', products: [], failed: false, loading: false });

  useEffect(() => {
    let current = true;
    setQuestions(null);
    setFailed(false);
    getQuestions(product.sap_code)
      .then((response) => {
        if (!current) return;
        const data = Array.isArray(response) ? response : response?.data;
        if (
          !Array.isArray(data) ||
          data.some(
            (question) =>
              !question ||
              question.id == null ||
              !Array.isArray(question.options)
          )
        )
          throw new Error('Questionário inválido');
        setQuestions(data);
      })
      .catch(() => {
        if (current) setFailed(true);
      });
    return () => {
      current = false;
    };
  }, [product.sap_code, attempt]);

  const codes = useMemo(
    () =>
      JSON.stringify(
        Array.from(
          new Set(
            Object.values(answers)
              .flatMap((answer) => answer.productsToAdd)
              .map(String)
              .filter(Boolean)
          )
        ).sort()
      ),
    [answers]
  );
  useEffect(() => {
    let current = true;
    const requested: string[] = JSON.parse(codes);
    if (!requested.length) {
      setExtrasState({
        key: codes,
        products: [],
        loading: false,
        failed: false,
      });
      return;
    }
    setExtrasState({ key: codes, products: [], loading: true, failed: false });
    getProductsByCodes(requested)
      .then((data) => {
        if (!current) return;
        if (
          !Array.isArray(data) ||
          requested.some(
            (code) =>
              !data.some((item) => item && String(item.sap_code) === code)
          )
        )
          throw new Error('Produtos adicionais indisponíveis');
        setExtrasState({
          key: codes,
          products: data,
          loading: false,
          failed: false,
        });
      })
      .catch(() => {
        if (current)
          setExtrasState({
            key: codes,
            products: [],
            loading: false,
            failed: true,
          });
      });
    return () => {
      current = false;
    };
  }, [codes, extrasAttempt]);

  const extras = useMemo(() => {
    if (extrasState.key !== codes) return [];
    const result = new Map<string, SelectedItem>();
    Object.entries(answers).forEach(([question, answer]) =>
      answer.productsToAdd.forEach((code) => {
        const key = String(code);
        const item = extrasState.products.find(
          (entry) => String(entry.sap_code) === key
        );
        if (!item) return;
        const previous = result.get(key);
        if (previous) previous.quantity = (previous.quantity ?? 1) + 1;
        else
          result.set(key, {
            key,
            questionCode: question,
            name: item.description,
            detail: '',
            sapCode: key,
            quantity: 1,
          });
      })
    );
    return Array.from(result.values());
  }, [answers, codes, extrasState]);

  useEffect(() => {
    onConfigurationChange({
      flow: 'legacy',
      answers,
      models: {},
      items: extras,
      complete:
        questions !== null &&
        !failed &&
        legacy.complete &&
        extrasState.key === codes &&
        !extrasState.loading &&
        !extrasState.failed,
    });
  }, [
    answers,
    extras,
    questions,
    failed,
    legacy.complete,
    extrasState,
    codes,
    onConfigurationChange,
  ]);

  if (failed)
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={() => setAttempt((value) => value + 1)}>
            Tentar novamente
          </Button>
        }
      >
        Não foi possível carregar as perguntas.
      </Alert>
    );
  if (!questions) return <LoadingQuestions />;
  const extrasStatus =
    extrasState.key !== codes || extrasState.loading ? (
      <CircularProgress size={22} aria-label="Carregando produtos adicionais" />
    ) : extrasState.failed ? (
      <Alert
        severity="error"
        action={
          <Button onClick={() => setExtrasAttempt((value) => value + 1)}>
            Tentar novamente
          </Button>
        }
      >
        Não foi possível carregar os produtos adicionais.
      </Alert>
    ) : undefined;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          md: 'minmax(0, 40fr) minmax(0, 60fr)',
        },
        gap: 2,
        alignItems: 'start',
      }}
    >
      <ConfigureTile
        embedded
        initialProductCode={product.sap_code}
        questions={questions}
        onConfigurationChange={setLegacy}
      />
      <Box sx={{ minWidth: 0 }}>
        <DescriptionPanel
          embedded
          productId={product.id}
          product={product}
          extras={extras}
          extrasStatus={extrasStatus}
          blockNumber={(code) =>
            questions.find((question) => String(question.id).trim() === code)
              ?.step
          }
        />
      </Box>
    </Box>
  );
};

/** Mesmo critério da implantação: questionário novo quando disponível, legado no fallback. */
export const PatientEquipmentQuestionnaire = ({
  product,
  patient,
}: {
  product: Product;
  patient: PatientV2Detail;
}) => {
  const [configuration, setConfiguration] =
    useState<PatientEquipmentConfiguration | null>(null);
  const [review, setReview] = useState(false);
  const onNewConfiguration = useCallback(
    (value: Omit<PatientEquipmentConfiguration, 'flow'>) => {
      setConfiguration({ ...value, flow: 'new' });
    },
    []
  );
  const parsedCode = Number(product.sap_code);
  const code =
    Number.isFinite(parsedCode) && parsedCode > 0 ? parsedCode : undefined;
  const flow = useApiQuestionnaire(code);
  return (
    <Box
      component="section"
      aria-label="Configuração do equipamento"
      sx={{ minWidth: 0, pt: 2, borderTop: '1px solid #e7ece8' }}
    >
      <Box sx={{ display: review ? 'none' : 'block' }}>
        {code !== undefined &&
        flow.status !== 'done' &&
        flow.status !== 'error' ? (
          <LoadingQuestions />
        ) : flow.status === 'done' && flow.data ? (
          <BasketFlowScreen
            embedded
            productId={product.id}
            product={product}
            flow={flow.data}
            onConfigurationChange={onNewConfiguration}
          />
        ) : (
          <LegacyEquipmentQuestionnaire
            product={product}
            onConfigurationChange={setConfiguration}
          />
        )}
        {configuration?.complete && (
          <Button
            type="button"
            variant="contained"
            onClick={() => setReview(true)}
            sx={{ mt: 2 }}
          >
            Prosseguir para revisão
          </Button>
        )}
      </Box>
      {configuration && (
        <Box sx={{ display: review ? 'block' : 'none' }}>
          <PatientEquipmentReview
            patient={patient}
            product={product}
            configuration={configuration}
            onBack={() => setReview(false)}
          />
        </Box>
      )}
    </Box>
  );
};
